/**
 * What `InputCore` is told: the player's options (08 §4) once, and each tick a `CoreFrame` with where the ranger
 * is, what the pointer is on and the gamepad. Pure data plus the one decision that is only about the frame -
 * where the pointer aims (ROUND7 §2).
 */

import type { WeaponId } from '../sim/types';
import { type AimBody, aimPick, BODY_HALF_WIDTH } from './aimPick';
import { type AimVolume, aimVolume } from './aimVolume';
import type { PadState } from './gamepadMap';

export interface InputOptions {
  /** Run is a toggle (the original, and the default) rather than held (08 §4). */
  runToggle: boolean;
  /** Crawl likewise. */
  crawlToggle: boolean;
  /** ROUND7: the crouched walk likewise; a toggle by default, as crawl is. */
  sneakToggle: boolean;
  /**
   * This ranger's `SimMods.grenadeRange`. The input layer has to know it: `heavy.ts` throws
   * `min(his reach, cmd.aimDist)`, so a crosshair that stops at the base range makes Long Arm's
   * "+25 % grenade throw range" unbuyable standing up, whatever the perk table says (R6).
   * It is the sim's own resolved figure, handed over at `startMission`, never a second copy.
   */
  grenadeRange: number;
}

export const DEFAULT_INPUT_OPTIONS: Readonly<InputOptions> = {
  runToggle: true, crawlToggle: true, sneakToggle: true, grenadeRange: 1,
};

/** What `step` needs to know about the world this tick. */
export interface CoreFrame {
  rangerX: number;
  rangerZ: number;
  /** The weapon in his hands before this command is applied. */
  current: WeaponId;
  /** What next / previous may land on; the five standard weapons when omitted. */
  available?: readonly WeaponId[] | undefined;
  /** Ground point under the mouse cursor; null where there is no pointer. */
  pointer: { x: number; z: number } | null;
  /** The gamepad this tick; null when none is connected. */
  pad: PadState | null;
  /**
   * ROUND7 §2: the standing men the cursor can land on (`aimTargets`) and the camera's height-to-ground factor
   * (`groundFactor`). With both, a cursor on a man aims at his feet; without, at the ground under the cursor.
   */
  targets?: readonly AimBody[] | undefined;
  /** The solids the cursor's line of sight can meet (`aimVolume`): props to destroy, machines, vehicles. */
  volumes?: readonly AimVolume[] | undefined;
  groundFactor?: number | undefined;
}

/**
 * Where the pointer aims: the feet of the man it is on, else the ground under it; null with no pointer.
 * ROUND7 §2: the ground under a cursor on a man is BEHIND him - 1.2 m behind at the chest at 45 deg - so a
 * stab from behind went into the air and a grenade flew long. Every weapon aims at his feet instead.
 */
export function pointerAim(frame: CoreFrame): { x: number; z: number } | null {
  const { pointer, targets, volumes, groundFactor } = frame;
  if (pointer === null) return null;
  if (groundFactor === undefined) return pointer;
  const man = targets === undefined ? null : aimPick(pointer, targets, groundFactor);
  // The aim audit: the same holds for everything else drawn standing up - a truck, a tower, a wall (aimVolume.ts).
  const solid = volumes === undefined ? null : aimVolume(pointer, volumes, groundFactor);
  // Both under the cursor: whichever is drawn in front, which is the one nearer the camera - further south.
  if (man !== null && solid !== null) return solid.z > man.z + BODY_HALF_WIDTH ? solid : man;
  return man ?? solid ?? pointer;
}
